"use client";

import { Smile } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover.tsx";
import { EmojiPicker, EmojiPickerContent, EmojiPickerSearch } from "@/components/ui/emoji-picker";

interface EmojiPickerButtonProps {
  onEmojiSelect: (emoji: string) => void;
  className?: string;
  buttonClassName?: string;
  disabled?: boolean;
}

export function EmojiPickerButton({
  onEmojiSelect,
  className,
  buttonClassName,
  disabled = false,
}: EmojiPickerButtonProps) {
  const { t } = useTranslation(["chat"]);

  return (
    <div className={cn("relative z-[100]", className)}>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild />
          <TooltipContent side="top">{t("emoji.addEmoji")}</TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <Popover modal={true}>
        <PopoverTrigger asChild>
          <Button
            className={cn(
              "text-muted-foreground hover:text-accent-foreground hover:bg-side-accent/20 h-7 w-7 rounded-full p-0",
              buttonClassName,
            )}
            disabled={disabled}
            type="button"
            variant="ghost"
          >
            <Smile className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="z-[500] p-0">
          <EmojiPicker
            className="h-[326px] rounded-lg shadow-md"
            onEmojiSelect={({ emoji }) => {
              onEmojiSelect(emoji);
            }}
          >
            <EmojiPickerSearch />
            <EmojiPickerContent />
          </EmojiPicker>
        </PopoverContent>
      </Popover>
    </div>
  );
}
